import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Printer, ArrowLeft, MapPin, Calendar, Users, FileText, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { getBookingDetail } from "../actions/tour/tourAction";
import Loading from "../common/Loading";
import { toast } from "@/components/ui/use-toast";
import { Paths } from "../constants/Paths";
import useSEO from "../hooks/useSEO";

export default function TourBookingDetail() {
    const { bookUID } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setSEO } = useSEO();
    const [bookingDetail, setBookingDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const { bookingSuccess, bookingData } = location.state || {};

    useEffect(() => {
        if (bookingDetail?.PackageTitle) {
            setSEO(`Booking Detail: ${bookingDetail.PackageTitle} | Yatra Tickets`);
        } else if (loading) {
            setSEO(`Loading Booking Detail... | Yatra Tickets`);
        } else {
            setSEO(`Booking Detail | Yatra Tickets`);
        }
    }, [bookingDetail, loading, setSEO]);

    useEffect(() => {
        //console.log("TourBookingDetail useEffect triggering. ID:", bookUID);
        if (!bookUID) return;

        //console.log("Fetching booking detail...");
        dispatch(getBookingDetail(bookUID, (data) => {
            //console.log("Booking Detail Fetched:", data);
            setBookingDetail(data);
            setLoading(false);

            if (bookingSuccess) {
                navigate(location.pathname, { replace: true, state: {} });
            }
        }));
    }, [bookUID, dispatch, bookingSuccess, navigate, location.pathname]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[50vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!bookingDetail) {
        return (
            <div className="container mx-auto p-8 text-center">
                <h2 className="text-xl font-bold text-red-500">Booking not found</h2>
                <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    const details = bookingDetail || {};
    const passengers = details.Customers || [];
    const images = details.tourPackFiles || [];

    const {
        IsGroup,
        PackageTitle,
        DestinationName,
        DepartureDate,
        TourFare,
        FlightFare,
        TotalFare,
        StatusID,
        BookingStatus,
        BookUID,
        TourBookID,
        PackageID,
        CreatedOn
    } = details;

    // Status mapping
    const getStatusBadge = (statusId, statusText) => {
        const sId = Number(statusId);
        let color = "bg-yellow-500";
        let text = statusText || "Pending";
        if (sId === 1 || statusText === "Booked") {
            color = "bg-green-500";
            text = "Booked";
        } else if (sId === 2 || statusText === "Paid") {
            color = "bg-blue-500";
            text = "Paid";
        }
        return <Badge className={`${color} text-white`}>{text}</Badge>;
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="container mx-auto p-4 lg:p-8 max-w-7xl">
            {/* Header / Actions */}
            <div className="flex items-center justify-between mb-6 print:hidden">
                <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back to Bookings
                </Button>
                <div className="flex gap-2">
                    {/* Pay Now button — only shown when StatusID === 1 (Booked, unpaid) */}
                    {StatusID === 1 && (
                        <Button
                            onClick={() =>
                                navigate(Paths.TOUR_PAYMENT, {
                                    state: {
                                        tourBookId: TourBookID,
                                        bookUID: BookUID,
                                        packageTitle: PackageTitle,
                                        totalFare: (TotalFare || 0) * passengers.length,
                                        travelerCount: passengers.length,
                                    },
                                })
                            }
                            className="gap-2 px-2 lg:px-4"
                        >
                            <CreditCard className="h-4 w-4" /> Pay Now
                        </Button>
                    )}
                    <Button onClick={handlePrint} className="px-2 lg:px-4">
                        <Printer className="h-4 w-4" /> Print
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column: Main Booking Info & Passengers */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Booking Header Card */}
                    <Card className="print:shadow-none print:border-none">
                        <CardHeader className="pb-4 border-b">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        {getStatusBadge(StatusID, BookingStatus)}
                                        {IsGroup ? (
                                            <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-[#f3e8ff] py-1.5 px-3 font-sans text-xs font-bold uppercase text-[#7e22ce]">
                                                Group
                                            </div>
                                        ) : (
                                            <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-[#e0e7ff] py-1.5 px-3 font-sans text-xs font-bold uppercase text-[#4338ca]">
                                                Fixed
                                            </div>
                                        )}
                                        <span className="text-sm text-muted-foreground">Booked on: {CreatedOn ? format(new Date(CreatedOn), "PPP") : "N/A"}</span>
                                    </div>
                                    <CardTitle className="text-2xl font-bold">
                                        <Link
                                            to={`/tour/package/${PackageID}/${encodeURIComponent(PackageTitle || '')}`}
                                            className="hover:text-primary hover:underline transition-colors cursor-pointer"
                                        >
                                            {PackageTitle}
                                        </Link>
                                    </CardTitle>
                                    <div className="flex items-center gap-2 text-muted-foreground mt-1">
                                        <MapPin className="h-4 w-4" />
                                        <span>{DestinationName}</span>
                                    </div>
                                </div>
                                {/* <div className="text-right">
                                    <div className="text-sm text-muted-foreground mb-1">Booking Ref</div>
                                    <div className="font-mono font-bold text-lg bg-muted px-2 py-1 rounded">{BookUID || TourBookID}</div>
                                </div> */}
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Calendar className="h-4 w-4" /> Departure Date
                                    </div>
                                    <div className="font-medium text-lg">
                                        {DepartureDate ? format(new Date(DepartureDate), "PPP") : "N/A"}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Users className="h-4 w-4" /> Total Travelers
                                    </div>
                                    <div className="font-medium text-lg">
                                        {passengers.length} Person(s)
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Passenger List */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="h-5 w-5" /> Passenger Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Full Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Contact</TableHead>
                                        {/* <TableHead>Email</TableHead> */}
                                        <TableHead>Date of Birth</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {passengers.map((pax, index) => (
                                        <TableRow key={index}>
                                            <TableCell className="font-medium">
                                                <div className="capitalize">
                                                    {(pax.CustomerTitle || pax.Title || "")} {(pax.FirstName || "")} {(pax.LastName || "")}
                                                </div>
                                            </TableCell>
                                            <TableCell>{pax.PaxType || "Adult"}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-col text-sm">
                                                    <span>{pax.ContactNo || pax.MobileNumber || "-"}</span>
                                                    <span className="text-xs text-muted-foreground">{pax.Email}</span>
                                                </div>
                                            </TableCell>
                                            {/* <TableCell>{pax.Email || "-"}</TableCell> */}
                                            <TableCell>{pax.DOB ? format(new Date(pax.DOB), "PP") : "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column: Images & Price */}
                <div className="space-y-6">
                    {/* Image Card */}
                    {images.length > 0 && (
                        <Card className="overflow-hidden border-0 shadow-lg">
                            <div className="aspect-video w-full relative">
                                <img
                                    src={images[0].FileURL}
                                    alt={PackageTitle}
                                    className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                            {images.length > 1 && (
                                <div className="grid grid-cols-4 gap-1 p-1 bg-muted/50">
                                    {images.slice(0, 4).map((img, idx) => (
                                        <div key={idx} className="aspect-square relative overflow-hidden rounded-sm">
                                            <img src={img.FileURL} alt="gallery" className="object-cover w-full h-full" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Pricing Card */}
                    <Card className="shadow-md border-primary/10">
                        <CardHeader className="bg-primary/5 pb-3">
                            <CardTitle className="text-lg">Fare Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 space-y-4">
                            {/* <div className="space-y-2">
                                <div className="flex justify-between text-sm items-center">
                                    <span className="text-muted-foreground">Tour Fare <span className="text-xs">({passengers.length}x)</span></span>
                                    <span className="font-medium">{new Intl.NumberFormat("en-US", { style: "currency", currency: "NPR" }).format(TourFare || 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm items-center">
                                    <span className="text-muted-foreground">Flight Fare <span className="text-xs">({passengers.length}x)</span></span>
                                    <span className="font-medium">{new Intl.NumberFormat("en-US", { style: "currency", currency: "NPR" }).format(FlightFare || 0)}</span>
                                </div>
                            </div> */}
                            <div className="flex justify-between text-sm items-center">
                                <span className="text-muted-foreground">Fare (Per Person) <span className="text-xs"></span></span>
                                <span className="font-medium">{new Intl.NumberFormat("en-US", { style: "currency", currency: "NPR" }).format(TotalFare || 0)}</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="font-bold text-lg text-center">Total Fare</span>
                                    <span className="text-[10px] text-muted-foreground block">(Airfare Included)</span>
                                </div>
                                <div className="text-right">
                                    <span className="block font-bold text-2xl text-primary">
                                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "NPR" }).format(TotalFare * passengers.length || 0)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">Incl. all taxes</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
